import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import {
  ArrowUpIcon,
  CheckBadgeIcon,
  PlusIcon,
  CheckCircleIcon,
  TruckIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import { updateUrlWithException } from "../../../components/Utils";
import { debounce } from "lodash";
import { getVehiclesAsync } from "../../../slices/vehicle";
import { VehiclesTable } from "../../../components/dashboard/vehicles/vehicles-table";
import { ModuleFilter } from "../../../components/utils/ModuleFilter";
import { VehicleForm } from "../../../components/dashboard/vehicles/vehicle-form";

export const VehiclesList = () => {
  const dispatch = useDispatch();
  const vehiclesState = useSelector((store) => {
    return store.vehicles;
  });

  const { isLoading } = vehiclesState;
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchParams] = useSearchParams();

  const [controller, setController] = useState({
    filters: [{ name: "active", value: true }],
    per_page: 25,
    page: searchParams.get("page") ? searchParams.get("page") : 1,
    query: searchParams.get("query") ? searchParams.get("query") : "",
    sort: searchParams.get("sort") ? searchParams.get("sort") : "asc",
    sortBy: searchParams.get("sortBy") ? searchParams.get("sortBy") : "created_at",
    view: searchParams.get("view") ? searchParams.get("view") : "all",
  });

  // Update all params from controller except filters (don't touch filters)
  useEffect(() => {
    updateUrlWithException(
      // These will be updated
      {
        page: controller.page,
        query: controller.query,
        sort: controller.sort,
        sortBy: controller.sortBy,
        view: controller.view,
      },
      // This will be preserved
      "filters"
    );
  }, [controller]);

  const handlePageChange = (newPage) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleFiltersChange = (filters) => {
    // Convert ModuleFilter format to backend format
    const backendFilters = [];

    // Handle custom filters
    if (filters.active) {
      backendFilters.push({
        name: "active",
        value: true,
      });
    }

    if (filters.with_odometer) {
      backendFilters.push({
        name: "with_odometer",
        value: true,
      });
    }

    if (filters.with_driver) {
      backendFilters.push({
        name: "with_driver",
        value: true,
      });
    }

    // Handle status filters
    if (filters.statuses && filters.statuses.length > 0) {
      filters.statuses.forEach((status) => {
        backendFilters.push({
          name: "status",
          value: status,
        });
      });
    }

    setController({
      ...controller,
      page: 1,
      query: filters.query !== undefined ? filters.query : controller.query,
      filters: backendFilters,
    });
  };

  // Define custom checkbox button filters for vehicles
  const customCheckboxButtonFilters = [
    {
      key: "active",
      label: "Active Vehicles",
      icon: CheckCircleIcon,
      defaultValue: true,
    },
    {
      key: "with_odometer",
      label: "With Odometer",
      icon: TruckIcon,
      defaultValue: false,
    },
    {
      key: "with_driver",
      label: "With Driver",
      icon: EyeIcon,
      defaultValue: false,
    },
  ];

  const debouncedDispatchAction = useCallback(
    debounce((controller) => {
      dispatch(getVehiclesAsync(controller));
    }, 250),
    [dispatch]
  );

  const refreshVehicles = useCallback(() => {
    dispatch(getVehiclesAsync(controller));
  }, [dispatch, controller]);

  useEffect(() => {
    // Trigger the debounced dispatch action
    debouncedDispatchAction(controller);

    // Clean the debounced function on component unmount.
    return () => {
      debouncedDispatchAction.cancel();
    };
  }, [controller, debouncedDispatchAction]);

  // Convert backend filters to ModuleFilter format
  const getInitialFilters = () => {
    const initialFilters = {
      query: controller.query,
      statuses: [],
      active: false,
      with_odometer: false,
      with_driver: false,
    };

    if (controller.filters && controller.filters.length > 0) {
      controller.filters.forEach((filter) => {
        switch (filter.name) {
          case "active":
            if (filter.value === true) {
              initialFilters.active = true;
            }
            break;
          case "with_odometer":
            if (filter.value === true) {
              initialFilters.with_odometer = true;
            }
            break;
          case "with_driver":
            if (filter.value === true) {
              initialFilters.with_driver = true;
            }
            break;
          case "status":
            initialFilters.statuses.push(filter.value);
            break;
        }
      });
    }

    return initialFilters;
  };

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  const headerActions = [];

  const headerMeta = [
    {
      label: vehiclesState.count + " Vehicles",
      description: "Total vehicles",
      path: null,
      action: null,
      icon: CheckBadgeIcon,
      textColor: "text-teal-700",
      hidden: false,
    },
    {
      label: vehiclesState.activeCount + " Active",
      description: "Active vehicles",
      path: null,
      action: null,
      icon: ArrowUpIcon,
      textColor: "text-teal-700",
      hidden: false,
    },
    {
      label: vehiclesState.withDriverCount + " With Driver",
      description: "Vehicles with assigned driver",
      path: null,
      action: null,
      icon: ArrowUpIcon,
      textColor: "text-teal-700",
      hidden: false,
    },
  ];

  return (
    <>
      <DashboardHeader
        subtitle={
          selectedItems.length > 0
            ? `${selectedItems.length} vehicle${
                selectedItems.length > 1 ? "s" : ""
              } selected`
            : ""
        }
        actions={headerActions}
        title={"Vehicles"}
        meta={headerMeta}
        mainButton={{
          label: "New Vehicle",
          action: () => {
            setSelectedVehicle(null);
            setEditModalOpen(true);
          },
          path: null,
          color: "",
          icon: PlusIcon,
        }}
      >
        <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
            <ModuleFilter
              className={"mt-10"}
              disabled={isLoading}
              onFiltersChange={handleFiltersChange}
              initialFilters={getInitialFilters()}
              searchPlaceholder="Search vehicles..."
              filterTitle="Filter Vehicles"
              customCheckboxButtonFilters={customCheckboxButtonFilters}
              acceptedStatuses={["active", "inactive"]} // Vehicle statuses
            />

            <VehiclesTable
              onItemsSelect={(items) => setSelectedItems(items)}
              itemState={vehiclesState}
              itemsCount={vehiclesState.count}
              currentCount={vehiclesState.currentCount}
              page={controller.page}
              onPageChange={handlePageChange}
              perPage={controller.per_page}
              fleetAutomileActions
              onRefreshVehicles={refreshVehicles}
              onEditVehicle={(vehicle) => {
                setSelectedVehicle(vehicle);
                setEditModalOpen(true);
              }}
            />
          </div>
        </div>
      </DashboardHeader>

      {/* Vehicle Form Dialog */}
      {editModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">
                {selectedVehicle ? "Edit Vehicle" : "Create New Vehicle"}
              </h3>
              <button
                onClick={() => setEditModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <VehicleForm
              vehicle={selectedVehicle}
              onClose={(success) => {
                setEditModalOpen(false);
                if (success) {
                  // Refresh the vehicles list
                  dispatch(getVehiclesAsync(controller));
                }
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};
